import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Play, Trash2, Edit, Clock, Mail, BarChart2, Download, RefreshCw, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_LABELS = { daily:'Дневно', weekly:'Седмично', monthly:'Месечно', yearly:'Годишно' };
const FORMAT_LABELS = { email_table:'📧 Имейл', pdf:'📄 PDF', excel:'📊 Excel' };
const SECTIONS = [
  { id:'inquiries', label:'Запитвания',     icon:'📩', desc:'Брой, статус, нови за периода' },
  { id:'contacts',  label:'CRM Контакти',   icon:'👥', desc:'Нови контакти, статуси' },
  { id:'projects',  label:'Проекти',        icon:'🏗️', desc:'Активни, завършени, в строеж' },
  { id:'listings',  label:'Обяви за имоти', icon:'🏠', desc:'Активни обяви, нови за периода' },
];

const ACTION_COLORS = {
  login:                { dot:'bg-blue-400',   text:'text-blue-300',   label:'ВХОД' },
  logout:               { dot:'bg-gray-500',   text:'text-gray-400',   label:'ИЗХОД' },
  login_failed:         { dot:'bg-red-500',    text:'text-red-400',    label:'ГРЕШЕН ВХОД' },
  create_property:      { dot:'bg-green-400',  text:'text-green-400',  label:'НОВ ПРОЕКТ' },
  update_property:      { dot:'bg-yellow-400', text:'text-yellow-300', label:'РЕДАКЦИЯ ПРОЕКТ' },
  delete_property:      { dot:'bg-red-500',    text:'text-red-400',    label:'ИЗТРИ ПРОЕКТ' },
  create_listing:       { dot:'bg-green-400',  text:'text-green-400',  label:'НОВА ОБЯВА' },
  update_listing:       { dot:'bg-yellow-400', text:'text-yellow-300', label:'РЕДАКЦИЯ ОБЯВА' },
  delete_listing:       { dot:'bg-red-500',    text:'text-red-400',    label:'ИЗТРИ ОБЯВА' },
  upload_file:          { dot:'bg-purple-400', text:'text-purple-300', label:'КАЧЕН ФАЙЛ' },
  delete_file:          { dot:'bg-red-500',    text:'text-red-400',    label:'ИЗТРИТ ФАЙЛ' },
  update_site_settings: { dot:'bg-orange-400', text:'text-orange-300', label:'НАСТРОЙКИ' },
  create_user:          { dot:'bg-green-400',  text:'text-green-400',  label:'НОВ ПОТРЕБИТЕЛ' },
  update_user:          { dot:'bg-yellow-400', text:'text-yellow-300', label:'РЕДАКЦИЯ ПОТРЕБИТЕЛ' },
  update_inquiry_status:{ dot:'bg-blue-400',   text:'text-blue-300',   label:'СТАТУС ЗАПИТВАНЕ' },
  run_report:           { dot:'bg-cyan-400',   text:'text-cyan-300',   label:'РЕПОРТ' },
};

function getStyle(action) {
  return ACTION_COLORS[action] || { dot:'bg-gray-500', text:'text-gray-400', label: action.replace(/_/g,' ').toUpperCase() };
}

function formatTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('bg-BG', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' });
}

function timeAgo(d) {
  if (!d) return 'Никога';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Днес';
  if (days === 1) return 'Вчера';
  return `преди ${days} дни`;
}

export default function AdminReportsAndLogs() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, data: null });
  const [running, setRunning] = useState({});
  const [preview, setPreview] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logUsers, setLogUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [total, setTotal] = useState(0);
  const [live, setLive] = useState(true);
  const liveRef = useRef(true);

  useEffect(() => { fetchReports(); fetchLogUsers(); }, []);

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (filterUser) params.append('user_id', filterUser);
      if (filterAction) params.append('action', filterAction);
      if (search) params.append('search', search);
      const r = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error('Logs error:', e);
    } finally {
      if (!silent) setLogsLoading(false);
    }
  }, [filterUser, filterAction, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (liveRef.current) fetchLogs(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  const fetchReports = async () => {
    try {
      const r = await axios.get(`${API}/reports`);
      setReports((r.data || []).filter(r => r.is_active));
    } catch (e) {
      console.error('Reports error:', e);
      toast.error('Грешка при зареждане на репорти');
    } finally { setReportsLoading(false); }
  };

  const fetchLogUsers = async () => {
    try {
      const r = await axios.get(`${API}/audit-logs/users`);
      setLogUsers(r.data || []);
    } catch {}
  };

  const runReport = async (id) => {
    setRunning(r => ({ ...r, [id]: true }));
    try {
      const r = await axios.post(`${API}/reports/${id}/run`);
      const sent = r.data.sent_to;
      toast.success(sent?.length ? `Изпратен до ${sent.join(', ')}` : 'Репортът е генериран');
      setPreview(r.data.html);
      fetchReports();
    } catch { toast.error('Грешка при генериране'); }
    finally { setRunning(r => ({ ...r, [id]: false })); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Изтриване?')) return;
    try { await axios.delete(`${API}/reports/${id}`); fetchReports(); }
    catch { toast.error('Грешка'); }
  };

  const downloadLogs = () => {
    const lines = [...logs].reverse().map(l =>
      `${formatTime(l.created_at)} | ${l.user_name} (${l.user_role}) | ${l.action_label} | ${l.entity_name || ''} | IP: ${l.ip || ''} | ${l.browser || ''}`
    ).join('\n');
    const blob = new Blob([`GREENCASTLE AUDIT LOG\nГенериран: ${new Date().toLocaleString('bg-BG')}\nЗаписи: ${logs.length}\n${'─'.repeat(80)}\n${lines}`], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-heading font-bold mb-6">{t('reports.title')}</h1>

      <div className="grid grid-cols-2 gap-6" style={{ height: 'calc(100vh - 160px)' }}>

        {/* ── LEFT: Reports ── */}
        <div className="flex flex-col min-h-0 bg-white border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-none">
            <h2 className="font-semibold flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-[#1B5E37]" /> Репорти
            </h2>
            <Button size="sm" onClick={() => setModal({ open: true, data: null })}
              className="bg-[#1B5E37] hover:bg-[#154a2c]">
              <Plus className="w-4 h-4 mr-1" /> Нов репорт
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {reportsLoading ? (
              <p className="text-sm text-muted-foreground text-center py-12">Зареждане...</p>
            ) : reports.length === 0 ? (
              <div className="text-center py-16">
                <BarChart2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">{t('reports.no_reports')}</p>
                <Button variant="outline" onClick={() => setModal({ open: true, data: null })}>
                  <Plus className="w-4 h-4 mr-2" /> Създай репорт
                </Button>
              </div>
            ) : reports.map(report => (
              <div key={report.id} className="border border-border rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <p className="font-semibold text-sm">{report.name}</p>
                  <div className="flex gap-1">
                    <button onClick={() => setModal({ open: true, data: report })}
                      className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-secondary">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteReport(report.id)}
                      className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {report.description && <p className="text-xs text-muted-foreground mb-2">{report.description}</p>}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="text-xs bg-[#1B5E37]/10 text-[#1B5E37] px-2 py-0.5 rounded-full">{PERIOD_LABELS[report.period]}</span>
                  <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{FORMAT_LABELS[report.format]}</span>
                  {report.schedule_enabled && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">🕐 По график</span>}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {timeAgo(report.last_run_at)}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => runReport(report.id)} disabled={running[report.id]}>
                    <Play className="w-3 h-3 mr-1" />
                    {running[report.id] ? 'Генерира...' : 'Изпълни'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Logs terminal ── */}
        <div className="flex flex-col min-h-0 bg-gray-950 rounded-xl overflow-hidden border border-gray-800">
          {/* Terminal title bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-800 flex-none">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-gray-400 text-xs font-mono">audit.log — {total} записа</span>
              {live && (
                <span className="flex items-center gap-1.5 text-green-400 text-xs font-mono">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
                  LIVE
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setLive(l => !l)}
                className={`text-xs px-2.5 py-1 rounded border font-mono transition-colors ${live ? 'border-green-600 text-green-400 hover:bg-green-900/30' : 'border-gray-600 text-gray-400 hover:bg-gray-800'}`}>
                {live ? '⏸ Пауза' : '▶ Live'}
              </button>
              <button onClick={() => fetchLogs()}
                className="text-gray-400 hover:text-white p-1.5 rounded hover:bg-gray-800" title="Обнови">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button onClick={downloadLogs}
                className="text-gray-400 hover:text-white p-1.5 rounded hover:bg-gray-800" title="Свали лога">
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/60 border-b border-gray-800 flex-none">
            <div className="relative flex-1">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Търси..."
                className="w-full bg-gray-800 text-gray-300 text-xs pl-7 pr-3 py-1.5 rounded border border-gray-700 focus:outline-none focus:border-gray-500 font-mono" />
            </div>
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              className="bg-gray-800 text-gray-300 text-xs px-2 py-1.5 rounded border border-gray-700 focus:outline-none font-mono">
              <option value="">Всички</option>
              {logUsers.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)}
              className="bg-gray-800 text-gray-300 text-xs px-2 py-1.5 rounded border border-gray-700 focus:outline-none font-mono">
              <option value="">Всички действия</option>
              <option value="login">Вход</option>
              <option value="login_failed">Грешен вход</option>
              <option value="create_property">Нов проект</option>
              <option value="update_property">Редакция</option>
              <option value="delete_property">Изтриване</option>
              <option value="upload_file">Файл</option>
              <option value="update_site_settings">Настройки</option>
            </select>
          </div>

          {/* Log entries */}
          <div className="flex-1 overflow-y-auto p-3 space-y-0.5 font-mono text-xs">
            {logsLoading ? (
              <p className="text-gray-500 text-center py-12 font-mono">Зареждане...</p>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 font-mono">~ няма записи ~</p>
                <p className="text-gray-600 text-[10px] mt-2">Действия ще се появят след влизане и редакции</p>
              </div>
            ) : [...logs].reverse().map((log, i) => {
              const style = getStyle(log.action);
              return (
                <div key={log.id || i}
                  className="flex items-start gap-2 py-1 px-2 rounded hover:bg-white/5 group transition-colors">
                  <div className={`w-2 h-2 rounded-full mt-1 flex-none ${style.dot}`} />
                  <span className="text-gray-600 flex-none w-36 shrink-0">{formatTime(log.created_at)}</span>
                  <span className={`flex-none font-bold ${style.text}`}>[{style.label}]</span>
                  <span className="text-gray-200 flex-none">{log.user_name}</span>
                  <span className="text-gray-600 flex-none text-[10px] mt-0.5">({log.user_role})</span>
                  <span className="text-gray-400">{log.action_label}</span>
                  {log.entity_name && (
                    <span className="text-white font-semibold">"{log.entity_name}"</span>
                  )}
                  <div className="hidden group-hover:flex items-center gap-2 ml-auto text-[10px] text-gray-600 flex-none">
                    {log.ip && <span>IP:{log.ip}</span>}
                    {log.browser && <span>{log.browser}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center px-5 py-3 border-b">
              <h3 className="font-semibold">{t('reports.view_report')}</h3>
              <button onClick={() => setPreview(null)} className="text-muted-foreground hover:text-foreground text-xl">✕</button>
            </div>
            <div className="p-5" dangerouslySetInnerHTML={{ __html: preview }} />
          </div>
        </div>
      )}

      <ReportModal
        key={modal.data?.id || 'new'}
        open={modal.open}
        data={modal.data}
        onClose={() => setModal({ open: false, data: null })}
        onSave={() => { setModal({ open: false, data: null }); fetchReports(); }}
      />
    </div>
  );
}

function ReportModal({ open, data, onClose, onSave }) {
  const { t } = useTranslation();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const emptyForm = { name:'', description:'', period:'monthly', format:'email_table',
    sections:['inquiries','contacts','projects','listings'],
    schedule_enabled:false, schedule_period:'monthly', schedule_hour:8, recipients:[] };

  const [form, setForm] = useState(emptyForm);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailName, setNewEmailName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(data ? {
      name: data.name||'', description: data.description||'',
      period: data.period||'monthly', format: data.format||'email_table',
      sections: data.sections?.length ? data.sections : ['inquiries','contacts','projects','listings'],
      schedule_enabled: data.schedule_enabled||false,
      schedule_period: data.schedule_period||data.period||'monthly',
      schedule_hour: data.schedule_hour||8,
      recipients: data.recipients||[],
    } : emptyForm);
  }, [data, open]);

  const toggleSection = id => setForm(f => ({
    ...f, sections: f.sections.includes(id) ? f.sections.filter(s=>s!==id) : [...f.sections, id]
  }));

  const addRecipient = () => {
    if (!newEmail.trim()) return;
    setForm(f => ({...f, recipients:[...f.recipients,{email:newEmail.trim(),name:newEmailName.trim()}]}));
    setNewEmail(''); setNewEmailName('');
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Въведи ime'); return; }
    if (!form.sections.length) { toast.error('Избери поне едно съдържание'); return; }
    setSaving(true);
    try {
      if (data?.id) await axios.put(`${API}/reports/${data.id}`, form);
      else await axios.post(`${API}/reports`, form);
      toast.success(data ? 'Репортът е обновен' : 'Репортът е създаден');
      onSave(); // This calls fetchReports() in parent
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Грешка: ' + (e?.response?.data?.detail || e.message || 'Неизвестна грешка'));
    }
    finally { setSaving(false); }
  };

  const SCHEDULE_PERIODS = [{v:'daily',l:'Дневно'},{v:'weekly',l:'Седмично'},{v:'monthly',l:'Месечно'},{v:'yearly',l:'Годишно'}];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{data ? 'Редактиране' : 'Нов репорт'}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Име *</Label>
            <Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Месечен репорт" />
          </div>
          <div>
            <Label>{t('admin.description')}</Label>
            <Textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} placeholder="По желание..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('reports.period')}</Label>
              <Select value={form.period} onValueChange={v=>setForm(f=>({...f,period:v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Последния ден</SelectItem>
                  <SelectItem value="weekly">Последната седмица</SelectItem>
                  <SelectItem value="monthly">Последния месец</SelectItem>
                  <SelectItem value="yearly">Последната година</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('reports.format')}</Label>
              <Select value={form.format} onValueChange={v=>setForm(f=>({...f,format:v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email_table">📧 Таблица в имейл</SelectItem>
                  <SelectItem value="pdf">📄 PDF</SelectItem>
                  <SelectItem value="excel">📊 Excel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Съдържание</Label>
            <div className="space-y-2">
              {SECTIONS.map(s => (
                <label key={s.id} onClick={()=>toggleSection(s.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.sections.includes(s.id)?'border-[#1B5E37] bg-[#1B5E37]/5':'border-border hover:bg-secondary/40'}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-none ${form.sections.includes(s.id)?'bg-[#1B5E37] border-[#1B5E37]':'border-gray-300'}`}>
                    {form.sections.includes(s.id)&&<span className="text-white text-xs font-bold">✓</span>}
                  </div>
                  <span className="text-lg">{s.icon}</span>
                  <div><p className={`text-sm font-medium ${form.sections.includes(s.id)?'text-[#1B5E37]':''}`}>{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Получатели</Label>
            {form.recipients.map((r,i) => (
              <div key={i} className="flex items-center gap-2 mb-2 p-2 bg-secondary/30 rounded-lg text-sm">
                <Mail className="w-4 h-4 text-muted-foreground flex-none" />
                <span className="flex-1">{r.name?`${r.name} <${r.email}>`:r.email}</span>
                <button onClick={()=>setForm(f=>({...f,recipients:f.recipients.filter((_,j)=>j!==i)}))} className="text-red-400 text-xs px-1">✕</button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input placeholder="имейл *" value={newEmail} onChange={e=>setNewEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addRecipient()} className="flex-1" />
              <Input placeholder="Има" value={newEmailName} onChange={e=>setNewEmailName(e.target.value)} className="w-28" />
              <Button type="button" variant="outline" onClick={addRecipient} disabled={!newEmail.trim()}><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#1B5E37]" /> По график</p>
              <button type="button" onClick={()=>setForm(f=>({...f,schedule_enabled:!f.schedule_enabled}))}
                className={`relative w-12 h-6 rounded-full transition-colors flex-none ${form.schedule_enabled?'bg-[#1B5E37]':'bg-gray-300'}`}>
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${form.schedule_enabled?'translate-x-6':'translate-x-0'}`}/>
              </button>
            </div>
            {form.schedule_enabled && (
              <div className="space-y-3 pt-3 border-t border-border">
                <div className="grid grid-cols-4 gap-2">
                  {SCHEDULE_PERIODS.map(o=>(
                    <button key={o.v} type="button" onClick={()=>setForm(f=>({...f,schedule_period:o.v}))}
                      className={`p-2 rounded-lg border text-xs text-center transition-colors ${form.schedule_period===o.v?'border-[#1B5E37] bg-[#1B5E37]/5 text-[#1B5E37] font-semibold':'border-border hover:bg-secondary'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs flex-none">Час:</Label>
                  <Select value={String(form.schedule_hour)} onValueChange={v=>setForm(f=>({...f,schedule_hour:parseInt(v)}))}>
                    <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(h=>(
                        <SelectItem key={h} value={String(h)}>{String(h).padStart(2,'0')}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground">→ {SCHEDULE_PERIODS.find(p=>p.v===form.schedule_period)?.l} в {String(form.schedule_hour).padStart(2,'0')}:00</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Отказ</Button>
            <Button className="bg-[#1B5E37] hover:bg-[#154a2c]" onClick={save} disabled={saving||!form.name.trim()}>
              {saving?'Запазване...':data?'Запази':'Създай'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
